export enum AmcContractStatus {
  DRAFT = "DRAFT",
  READY_FOR_ACTIVATION = "READY_FOR_ACTIVATION",
  ACTIVE = "ACTIVE",
  /** Completed via a single manual action (POST /amc/:id/complete) — no end-date gating, callable any time from ACTIVE. */
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum AmcSourceType {
  QUOTATION = "QUOTATION",
  MANUAL = "MANUAL",
  RENEWAL = "RENEWAL",
  MIGRATION = "MIGRATION",
}

export enum AmcBillingType {
  FULL_UPFRONT = "FULL_UPFRONT",
}

export const AmcSourceTypeLabelMap: Record<AmcSourceType, string> = {
  [AmcSourceType.QUOTATION]: "Quotation",
  [AmcSourceType.MANUAL]: "Manual Entry",
  [AmcSourceType.RENEWAL]: "Renewal",
  [AmcSourceType.MIGRATION]: "Migrated Contract",
};

export const AmcBillingTypeLabelMap: Record<AmcBillingType, string> = {
  [AmcBillingType.FULL_UPFRONT]: "Full upfront",
};

/**
 * Line items stay editable while DRAFT (unless already populated from an
 * accepted quotation — those lines are invoice-breakdown detail only), and
 * while ACTIVE, so a corrected invoice can be generated via
 * regenerate-invoice. Once COMPLETED, only a Super Admin may still edit them
 * (everyone else is locked out). Locked at READY_FOR_ACTIVATION and
 * CANCELLED. Every other contract field (title, dates, client, visit count)
 * keeps its own separate, unchanged lock.
 */
export function amcAllowsLineItemUpdate(
  contract:
    | {
        status?: string | null;
        sourceType?: string | null;
        items?: unknown[] | null;
      }
    | null
    | undefined,
  isSuperAdmin = false,
): boolean {
  const status = contract?.status;
  if (!contract || status === AmcContractStatus.DRAFT) {
    const isQuotationWithItems =
      contract?.sourceType === AmcSourceType.QUOTATION &&
      (contract?.items?.length ?? 0) > 0;
    return !isQuotationWithItems;
  }
  if (status === AmcContractStatus.COMPLETED) return isSuperAdmin;
  return status === AmcContractStatus.ACTIVE;
}

/**
 * Whether the edit page should be reachable at all: DRAFT (to set up the
 * contract) or ACTIVE (line items remain correctable then, even though every
 * other field stays locked once the contract leaves DRAFT) are open to
 * anyone with update permission; COMPLETED is reachable only for a Super
 * Admin.
 */
export function amcAllowsEditPageAccess(
  status: string | null | undefined,
  isSuperAdmin = false,
): boolean {
  if (status === AmcContractStatus.COMPLETED) return isSuperAdmin;
  return (
    status === AmcContractStatus.DRAFT || status === AmcContractStatus.ACTIVE
  );
}

export const amcStatusMap = {
  [AmcContractStatus.DRAFT]: {
    label: "Draft",
    color: "secondary",
  },
  [AmcContractStatus.ACTIVE]: {
    label: "Active",
    color: "warning",
  },

  [AmcContractStatus.READY_FOR_ACTIVATION]: {
    label: "Ready For Activation",
    color: "success",
  },

  [AmcContractStatus.COMPLETED]: {
    label: "Completed",
    color: "primary",
  },
  [AmcContractStatus.CANCELLED]: {
    label: "Cancelled",
    color: "danger",
  },
} as const;
